/**
 * AlertThresholdsPanel
 *
 * US-24-GR-27 — Collapsible form on QueueHealthPage for editing the
 * configurable threshold alert config (GET/PUT /api/admin/orchestrator/alert-thresholds).
 * Dirty-state Save/Discard mirrors AiConfigPage's pattern.
 */

import { Fragment, useCallback, useEffect, useRef, useState, startTransition, type ReactNode } from 'react';
import { RefreshCw, AlertCircle, CheckCircle, ChevronDown, SlidersHorizontal } from 'lucide-react';
import {
  getAlertThresholds,
  putAlertThresholds,
  type AlertThresholdConfig,
} from '../api/orchestratorClient';
import { AbbrLabel } from './AbbrLabel';

type FieldKey = keyof AlertThresholdConfig;

type FieldGroup = 'Agent Tasks Queue' | 'Agent Events Queue' | 'General';

interface FieldSpec {
  key: FieldKey;
  label: ReactNode;
  hint: string;
  min: number;
  max?: number;
  group: FieldGroup;
}

const FIELDS: FieldSpec[] = [
  { key: 'queueDepthWarn', label: 'Agent Tasks Queue Depth — Warn', hint: 'Agent-tasks queue depth that turns the tile amber.', min: 0, group: 'Agent Tasks Queue' },
  { key: 'queueDepthAlert', label: 'Agent Tasks Queue Depth — Alert', hint: 'Agent-tasks queue depth that turns the tile red.', min: 0, group: 'Agent Tasks Queue' },
  { key: 'dlqDepthWarn', label: <>Agent Tasks <AbbrLabel term="DLQ" /> Depth — Warn</>, hint: 'Agent-tasks dead-letter queue depth that turns the tile amber.', min: 0, group: 'Agent Tasks Queue' },
  { key: 'dlqDepthAlert', label: <>Agent Tasks <AbbrLabel term="DLQ" /> Depth — Alert</>, hint: 'Agent-tasks dead-letter queue depth that turns the tile red.', min: 0, group: 'Agent Tasks Queue' },
  { key: 'eventsQueueDepthWarn', label: 'Agent Events Queue Depth — Warn', hint: 'Agent-events queue depth that turns the tile amber.', min: 0, group: 'Agent Events Queue' },
  { key: 'eventsQueueDepthAlert', label: 'Agent Events Queue Depth — Alert', hint: 'Agent-events queue depth that turns the tile red.', min: 0, group: 'Agent Events Queue' },
  { key: 'eventsDlqDepthWarn', label: <>Agent Events <AbbrLabel term="DLQ" /> Depth — Warn</>, hint: 'Agent-events dead-letter queue depth that turns the tile amber.', min: 0, group: 'Agent Events Queue' },
  { key: 'eventsDlqDepthAlert', label: <>Agent Events <AbbrLabel term="DLQ" /> Depth — Alert</>, hint: 'Agent-events dead-letter queue depth that turns the tile red.', min: 0, group: 'Agent Events Queue' },
  { key: 'successRateFloorPct', label: <><AbbrLabel term="AI" /> Success Rate Floor (%)</>, hint: 'Alert when the rolling success rate drops below this percentage.', min: 0, max: 100, group: 'General' },
  { key: 'windowMinutes', label: 'Evaluation Window (minutes)', hint: 'Rolling window used to evaluate the success-rate floor.', min: 1, group: 'General' },
];

function validate(draft: AlertThresholdConfig): Partial<Record<FieldKey, string>> {
  const errors: Partial<Record<FieldKey, string>> = {};
  for (const field of FIELDS) {
    const value = draft[field.key];
    if (Number.isNaN(value)) {
      errors[field.key] = 'Must be a number';
      continue;
    }
    if (value < field.min) {
      errors[field.key] = `Must be ${field.min} or greater`;
      continue;
    }
    if (field.max !== undefined && value > field.max) {
      errors[field.key] = `Must be ${field.max} or less`;
    }
  }
  if (
    !errors.queueDepthWarn &&
    !errors.queueDepthAlert &&
    draft.queueDepthAlert < draft.queueDepthWarn
  ) {
    errors.queueDepthAlert = 'Alert threshold must be ≥ warn threshold';
  }
  if (
    !errors.dlqDepthWarn &&
    !errors.dlqDepthAlert &&
    draft.dlqDepthAlert < draft.dlqDepthWarn
  ) {
    errors.dlqDepthAlert = 'Alert threshold must be ≥ warn threshold';
  }
  if (
    !errors.eventsQueueDepthWarn &&
    !errors.eventsQueueDepthAlert &&
    draft.eventsQueueDepthAlert < draft.eventsQueueDepthWarn
  ) {
    errors.eventsQueueDepthAlert = 'Alert threshold must be ≥ warn threshold';
  }
  if (
    !errors.eventsDlqDepthWarn &&
    !errors.eventsDlqDepthAlert &&
    draft.eventsDlqDepthAlert < draft.eventsDlqDepthWarn
  ) {
    errors.eventsDlqDepthAlert = 'Alert threshold must be ≥ warn threshold';
  }
  return errors;
}

interface AlertThresholdsPanelProps {
  /** Notifies the parent whenever the saved config changes so tile thresholds can update. */
  onConfigSaved?: (config: AlertThresholdConfig) => void;
}

export function AlertThresholdsPanel({ onConfigSaved }: AlertThresholdsPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const [config, setConfig] = useState<AlertThresholdConfig | null>(null);
  const [draft, setDraft] = useState<AlertThresholdConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // US-19-GR-81: captured so it can be cleared on unmount, instead of a bare setTimeout.
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const cfg = await getAlertThresholds();
      setConfig(cfg);
      setDraft(cfg);
      onConfigSaved?.(cfg);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load alert thresholds');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    startTransition(() => {
      void load();
    });
  }, [load]);

  const errors = draft ? validate(draft) : {};
  const hasErrors = Object.keys(errors).length > 0;
  const isDirty = !!draft && !!config && JSON.stringify(draft) !== JSON.stringify(config);

  async function handleSave() {
    if (!draft || hasErrors) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await putAlertThresholds(draft);
      setConfig(updated);
      setDraft(updated);
      setSaved(true);
      onConfigSaved?.(updated);
      savedTimerRef.current = setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save alert thresholds');
    } finally {
      setSaving(false);
    }
  }

  function setField(key: FieldKey, value: number) {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  }

  return (
    <section className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden" aria-label="Alert threshold configuration">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls="alert-thresholds-body"
        className="w-full flex items-center justify-between px-4 py-3 min-h-[48px] text-left hover:bg-zinc-800/40 transition-colors"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-zinc-200">
          <SlidersHorizontal className="w-4 h-4 text-violet-400" />
          Alert Thresholds
        </span>
        <ChevronDown
          className={`w-4 h-4 text-zinc-500 transition-transform ${expanded ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {expanded && (
        <div id="alert-thresholds-body" className="px-4 pb-4 border-t border-zinc-800">
          {loading && (
            <div className="flex items-center justify-center h-24">
              <RefreshCw className="w-4 h-4 text-zinc-500 animate-spin" />
            </div>
          )}

          {!loading && error && !draft && (
            <div className="flex items-center gap-3 p-3 mt-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-red-400 font-medium">Failed to load alert thresholds</p>
                <p className="text-xs text-zinc-500 mt-0.5">{error}</p>
              </div>
              <button
                type="button"
                onClick={() => void load()}
                className="px-3 py-1.5 text-xs rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors"
              >
                Retry
              </button>
            </div>
          )}

          {!loading && draft && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {FIELDS.map((field, index) => (
                <Fragment key={field.key}>
                  {field.group !== FIELDS[index - 1]?.group && (
                    <h3
                      className={`sm:col-span-2 text-xs font-semibold uppercase tracking-wide text-violet-400 pt-2 border-zinc-800 ${
                        index === 0 ? '' : 'border-t'
                      }`}
                    >
                      {field.group}
                    </h3>
                  )}
                  <div>
                    <label htmlFor={`threshold-${field.key}`} className="block text-xs font-medium text-zinc-400 mb-1">
                      {field.label}
                    </label>
                    <input
                      id={`threshold-${field.key}`}
                      type="number"
                      min={field.min}
                      max={field.max}
                      step={1}
                      value={draft[field.key]}
                      onChange={(e) => setField(field.key, parseFloat(e.target.value))}
                      aria-invalid={!!errors[field.key]}
                      aria-describedby={`threshold-${field.key}-hint`}
                      className={`w-32 px-3 py-2 rounded-lg bg-zinc-800 border text-sm text-zinc-200 focus:outline-none focus:ring-2 focus:ring-violet-500/50 min-h-[48px] ${
                        errors[field.key] ? 'border-red-500/60' : 'border-zinc-700 focus:border-violet-500'
                      }`}
                    />
                    <p id={`threshold-${field.key}-hint`} className="text-xs text-zinc-500 mt-1">
                      {field.hint}
                    </p>
                    {errors[field.key] && (
                      <p className="text-xs text-red-400 mt-1" role="alert">
                        {errors[field.key]}
                      </p>
                    )}
                  </div>
                </Fragment>
              ))}

              <div className="sm:col-span-2 flex items-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => void handleSave()}
                  disabled={saving || !isDirty || hasErrors}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 text-zinc-950 text-sm font-medium hover:bg-violet-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]"
                >
                  {saving && <RefreshCw className="w-4 h-4 animate-spin" />}
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>

                {saved && (
                  <span className="flex items-center gap-1.5 text-sm text-emerald-400">
                    <CheckCircle className="w-4 h-4" />
                    Saved
                  </span>
                )}

                {error && draft && (
                  <span className="flex items-center gap-1.5 text-sm text-red-400" role="alert">
                    {error}
                  </span>
                )}

                {isDirty && (
                  <button
                    type="button"
                    onClick={() => setDraft(config)}
                    className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
                  >
                    Discard changes
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default AlertThresholdsPanel;
